import { FormControl, FormLabel } from '@mui/material'
import { useEffect, useState } from 'react'
import { string } from 'yup'
import debounce from 'lodash/debounce'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import suburbsAPI from 'api/ApiActions/Filters/Suburbs/Action'
import {
  checkOptionValueBasedOnRegion,
  fetchOptionBasedOnRegion,
  getFormattedInputValue,
} from 'components/Participants/Filters/Suburbs/Helper'
import SingleSelectWithSearch from 'components/Shared/SingleSelectWithSearch'
import { setAnswer } from 'redux/slices/SignupFormAnswers'
import { getErrorHelperText } from 'lib/participantProfile/Helper'
import SingleLineQuestion from 'components/SignupFormQuestions/SingleLineQuestion'

export const validateRegionQuestion = async (
  answer: { value: string },
  question: QuestionsItems,
): Promise<string[]> => {
  let schema = string().nullable()
  if (question.required) {
    schema = schema.required()
  }
  try {
    await schema.validate(answer?.value)
    return []
  } catch (err: any) {
    return err.errors
  }
}

const RegionQuestion = ({
  question,
  id,
  readOnlyMode,
  required,
  answer,
}: RegionQuestionProps) => {
  const site = useAppSelector((state) => state.site.site)
  const region = site.region.toLowerCase()
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const errors = useAppSelector((state) => state.signupAnswers.errors)
  const error = errors[id] || []
  const dispatch = useAppDispatch()
  const [value, setValue] = useState(answer)

  const handleFetchSuburbs = async (searchTerm: string) => {
    setLoading(true)
    const results = await suburbsAPI.fetchSuburbs(searchTerm)
    setOptions(results.data.data)
    setLoading(false)
  }

  useEffect(() => {
    if (inputValue.length === 0) {
      setInputValue('')
      setOptions([])
      return
    }

    handleFetchSuburbs(inputValue)
  }, [inputValue])

  const handleInputChange = debounce((value: string) => {
    const formattedInputValue = getFormattedInputValue(region, value)
    setInputValue(formattedInputValue)
  }, 300)

  const handleChange = (value: any) => {
    setValue(value)
    if (value !== null) {
      const suburbString = fetchOptionBasedOnRegion(region, value)
      dispatch(
        setAnswer({
          questionId: id,
          answer: { value: suburbString },
        }),
      )
      return
    }
    dispatch(
      setAnswer({
        questionId: id,
        answer: { value: null },
      }),
    )
  }

  if (region === 'united_kingdom') {
    return (
      <SingleLineQuestion
        question={question}
        id={id}
        readOnlyMode={readOnlyMode}
        required={required}
        maxLength={255}
      />
    )
  }

  return (
    <FormControl fullWidth error={error.length > 0} required={required}>
      <FormLabel> {question} </FormLabel>
      <SingleSelectWithSearch
        options={options}
        loading={loading}
        required={required}
        question={question}
        value={answer || null}
        inputValue={inputValue}
        onFocus={() => {
          if (value !== null) setOptions([])
        }}
        getOptionLabel={(option: SuburbOption) =>
          fetchOptionBasedOnRegion(region, option)
        }
        isOptionEqualToValue={(option: SuburbOption, value: SuburbOption) => {
          return checkOptionValueBasedOnRegion(option, value, region)
        }}
        onInputChange={(_, newInputValue) => handleInputChange(newInputValue)}
        TextFieldParams={{
          testId: 'region-question',
          fullWidth: true,
          variant: 'standard',
          error: error.length > 0,
          helperText: getErrorHelperText(error, question),
        }}
        readOnly={readOnlyMode}
        onChange={(_, value) => handleChange(value)}
      />
    </FormControl>
  )
}

export default RegionQuestion
